import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import {
  getAmountStep,
  getMaxAmount,
  getMinAmount,
  normalizeAmount,
} from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import useCartItemTotal from '@features/cart/hooks/useCartItemTotal'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'


import { DEBOUNCE_TIMEOUT_UPDATE_AMOUNT } from '@shared/contants/product'
import {
  Product,
  useDropCartItemMutation,
  useUpdateCartProductAmountMutation,
} from '@shared/generated'
import { useProductUnit } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import Button, { type ButtonSizeVariant } from '@shared/ui/button'
import { formatPrice } from '@shared/uttils'


import styles from './styles.module.scss'

interface ProductCounterProps {
  shopId: string;
  className?: string;
  size?: ButtonSizeVariant;
  product: Product;
  invertInfo?: boolean;
  noTotals?: boolean;
  /**
   * Component will behave like a single block and take all required place
   * In practise this means we will disable absolute positioning for nested elements
   */
  asBlock?: boolean
}

export default function ProductCounter (props: ProductCounterProps) {
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { t: tcart } = useTranslation('cart')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { className, size, product, invertInfo, noTotals = false, asBlock = false } = props
  const { getProduct, addOrUpdateProduct, removeProduct, currentShopId } =
    useCartStore()
  const cartItem = getProduct(product.id)
  const { isAuthorized } = useAuth()
  const [ fetchUpdateCart ] = useUpdateCartProductAmountMutation()
  const [ fetchRemoveProduct ] = useDropCartItemMutation()
  const { addToCart: trackAddToCart, removeFromCart: trackRemoveFromCart } = useAnalytics()

  const amount = useMemo(() => cartItem?.amount || 0, [ cartItem?.amount ])
  const step = getAmountStep(product) as number
  const { maxAmount } = getMaxAmount(product)
  const debouncedUpdate = useDebounceCallback(
    fetchUpdateCart,
    DEBOUNCE_TIMEOUT_UPDATE_AMOUNT,
  )
  const cartItemTotalPrice = useCartItemTotal(product, amount)

  const handleIncrement = async () => {
    const newAmount = normalizeAmount(amount + step, step)

    if (newAmount <= maxAmount) {
      // Track add_to_cart for quantity increase
      trackAddToCart(product, step)

      if (isAuthorized) {
        try {
          await debouncedUpdate({
            variables: {
              shopId: currentShopId as string,
              itemData: {
                amount: newAmount,
                product_id: product.id,
                comment: cartItem?.comment,
              },
            },
          })

          addOrUpdateProduct({
            product_id: product.id,
            amount: newAmount,
          })
        }
        catch (error) {
          console.error('fetchUpdateCart error: ', error)
          openErrorInfoModal({
            description: te('update_cart_item'),
          })
        }
      }
      else {
        addOrUpdateProduct({
          product_id: product.id,
          amount: newAmount,
        })
      }
    }
  }

  const handleDecrement = async () => {
    const newAmount = normalizeAmount(amount - step, step)
    const min = getMinAmount(product)

    // Track remove_from_cart for quantity decrease
    trackRemoveFromCart(product, step)

    if (newAmount >= min) {
      if (isAuthorized) {
        try {
          await debouncedUpdate({
            variables: {
              shopId: currentShopId as string,
              itemData: {
                amount: newAmount,
                product_id: product.id,
                comment: cartItem?.comment,
              },
            },
          })

          addOrUpdateProduct({
            product_id: product.id,
            amount: newAmount,
          })
        }
        catch (error) {
          console.error('fetchUpdateCart error: ', error)
          openErrorInfoModal({
            description: te('update_cart_item'),
          })
        }
      }
      else {
        addOrUpdateProduct({
          product_id: product.id,
          amount: newAmount,
        })
      }
    }
    else {
      if (isAuthorized) {
        try {
          await fetchRemoveProduct({
            variables: {
              shopId: currentShopId as string,
              productId: product.id,
            },
          })

          removeProduct(product.id)
        }
        catch (error) {
          console.error('fetchRemoveProduct error: ', error)
          openErrorInfoModal({
            description: te('drop_cart_item'),
          })
        }
      }
      else {
        removeProduct(product.id)
      }
    }
  }

  const { unit, value } = useProductUnit({
    isWeightProduct: product.isWeightProduct as boolean,
    value: amount,
    price: cartItemTotalPrice as number,
    t: tc,
  })

  return (
    <div
      className={cn(styles.container, asBlock ? styles['as-block'] : null)}
    >
      <div
        className={cn(
          styles.control,
          className,
          size ? styles[size] : null,
          invertInfo ? 'product_counter_tabbar' : null,
        )}
      >
        <Button
          className={cn(
            styles['control__button'],
            'reset-button',
            styles.control__dec,
            {
              [styles.disabled]: amount <= 0,
            },
          )}
          type="button"
          size={size}
          theme="none"
          onClick={handleDecrement}
          disabled={amount <= 0}>
        </Button>
        <div className={styles.control__info}>
          <b>
            {value} {unit}
          </b>
        </div>
        <Button
          className={cn(
            styles['control__button'],
            'reset-button',
            styles.control__inc,
            {
              [styles.disabled]: amount >= maxAmount,
            },
          )}
          type="button"
          size={size}
          theme="none"
          onClick={handleIncrement}
          disabled={amount >= maxAmount}
        ></Button>
      </div>

      {!noTotals && <div className={cn(styles.totals)}>
        {invertInfo ? (
          <p>
            <b>
              {value} {unit}
            </b>
            {!!cartItemTotalPrice && (
              <span>{formatPrice(cartItemTotalPrice)}</span>
            )}
          </p>
        ) : (
          <p>
            <span>
              {!!cartItemTotalPrice && tcart('in_cart_amount', { total: formatPrice(cartItemTotalPrice), amount: value, unit })}
            </span>
          </p>
        )}
      </div>}
    </div>
  )
}
